import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import { Mitarbeiter } from '../../master-data-management/users/entities/Mitarbeiter.js';
import { Workshop } from './Workshop.js';
import { WorkshopAppointment } from './WorkshopAppointment.js';
import type { WorkshopAttendanceStatus } from './WorkshopAttendanceStatus.js';

/**
 * @entity WorkshopAttendance
 */
@Entity()
export class WorkshopTrainingAttendance extends MongoEntity {
  public static override _class = 'WorkshopTrainingAttendance';
  public static override _collectionName = 'workshop-attendance';

  @DataLoadedField(() => Mitarbeiter, ({ userID }) => userID)
  public user: Maybe<Mitarbeiter>;

  @Field(() => String)
  public userID: Maybe<string>;

  @Field(() => DateTime, { default: val => val ?? DateTime.now() })
  public createdAt: Maybe<DateTime>;

  @DataLoadedField(() => Workshop, ({ workshopID }) => workshopID)
  public workshop: Maybe<Workshop>;

  @validate(nonOptional())
  @Field(() => String)
  public workshopID: Maybe<string>;

  @Field(() => DateTime)
  public date: Maybe<DateTime>;

  @Field(() => String)
  public state: Maybe<WorkshopAttendanceStatus>;

  @Field(() => String)
  public accessToken: Maybe<string>;

  @Field(() => WorkshopAppointment, {
    resolve: function (this: EntityData<WorkshopTrainingAttendance>) {
      return async () => {
        const date = this.date instanceof DateTime ? this.date.toJSDate() : this.date;
        const col = await getRawCollection<EntityData<WorkshopAppointment>>(WorkshopAppointment._collectionName);
        const res = (await col.findOne({
          workshopID: this.workshopID,
          $and: [
            {
              start: {
                $gte: DateTime.fromJSDate(date ?? new Date())
                  .startOf('day')
                  .toJSDate(),
              },
            },
            {
              start: {
                $lte: DateTime.fromJSDate(date ?? new Date())
                  .endOf('day')
                  .toJSDate(),
              },
            },
          ],
        })) as WorkshopAppointment;
        return res;
      };
    },
  })
  public appointment: Maybe<WorkshopAppointment>;
}

if (A.isServer) {
  const attendance = getRawCollection<any>(WorkshopTrainingAttendance._collectionName);

  // Create DB Indices
  void attendance.then(async db => {
    void db.createIndex({ userID: 1, workshopID: 1 });
  });
}
